import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { IoCalendarOutline } from "react-icons/io5";
import styled from "styled-components";
import { cn } from "../../utils/lib";
import { ErrorMsg } from "./ErrorMsg";
import { Label } from "./Label";

const CalenderContainer = styled.div<{ size: "sm" | "md" | "lg" }>`
  .react-datepicker-wrapper {
    width: 100%;
  }
  .react-datepicker__input-container .react-datepicker__calendar-icon {
    top: 50%;
    transform: translateY(-50%);
  }
  .react-datepicker__view-calendar-icon input {
    padding: ${({ size }) =>
    size === "sm"
      ? "5px 10px 5px 28px"
      : size === "md"
        ? "8px 10px 8px 28px"
        : "10px 10px 10px 28px"};
  }
  .react-datepicker__calendar-icon {
    padding: 0 0 0 8px;
  }

  .react-datepicker__navigation--years-upcoming {
    background-color: #e2e8f0;
    width: 100%;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  .react-datepicker__navigation--years-previous {
    background-color: #e2e8f0;
    width: 100%;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

interface CustomDatePickerProps {
  selected: Date | null;
  onChange: (date: Date | null) => void;
  dateFormat?: string;
  isClearable?: boolean;
  placeholderText?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  minDate?: Date;
  maxDate?: Date;
  minTime?: Date;
  maxTime?: Date;
  filterTime?: (time: Date) => boolean;
  disabled?: boolean;
  shouldCloseOnSelect?: boolean;
  showTimeSelect?: boolean;
  timeFormat?: string;
  timeIntervals?: number;
  timeCaption?: string;
  showYearDropdown?: boolean;
  scrollableYearDropdown?: boolean;
  yearDropdownItemNumber?: number;
  showMonthDropdown?: boolean;
  filterDate?: (date: Date) => boolean;
  label?: string;
  required?: boolean;
  isError?: boolean;
  errorMessage?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  size?: "sm" | "md" | "lg";
}

const CustomDatePicker = ({
  selected,
  onChange,
  dateFormat = "yyyy-MM-dd",
  isClearable = false,
  placeholderText = "YYYY-MM-DD",
  onKeyDown,
  onBlur,
  label,
  required,
  disabled = false,
  isError,
  errorMessage,
  size = "md",
  ...props
}: CustomDatePickerProps) => {
  const SIZE = {
    sm: "text-[10px] 2xl:text-xs",
    md: "text-xs 2xl:text-sm",
    lg: "text-sm 2xl:text-base",
  };
  const ICON_SIZE = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  };
  return (
    <div className="w-full text-start">
      {label && (
        <Label text={label} required={required} className="mb-1.5 2xl:mb-2" />
      )}

      <CalenderContainer size={size}>
        <DatePicker
          selected={selected}
          onChange={onChange}
          dateFormat={dateFormat}
          isClearable={isClearable}
          disabled={disabled}
          placeholderText={placeholderText}
          onKeyDown={onKeyDown}
          className={cn(
            "w-full rounded-lg border bg-red-500 text-xs text-gray-700 placeholder:text-gray-300 outline-none transition-all duration-200 focus:border-primary focus:ring-1   focus:ring-primary 2xl:text-sm h-9",
            isError ? "border-red-500" : "border-gray-300",
            disabled
              ? "cursor-not-allowed bg-gray-100 text-gray-500"
              : "bg-white",
            SIZE[size],
          )}
          showIcon
          onBlur={onBlur}
          icon={<IoCalendarOutline className={cn("text-gray-300", ICON_SIZE[size])} />}
          clearButtonClassName="after:bg-transparent after:content-['⛌'] after:text-[10px] after:text-red-500"
          {...props}
        />
      </CalenderContainer>

      {isError && errorMessage && <ErrorMsg text={errorMessage} />}
    </div>
  );
};

export { CustomDatePicker };

